import { useEffect, useState } from 'react';
import {
  LocationPriorityRequestDto,
  LocationResponseDto,
} from '@tiendanube/common';
import { CATALOG_STOCK_TRANSFER } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { CancelAndConfirmButtons, Responsive } from 'commons/components';
import { useAsyncFunc, useModal } from 'commons/hooks';
import useInventoryAppliedFilters from 'domains/Catalog/Inventory/pages/hooks/useInventoryAppliedFilters';
import { trackingShippingDeleteDistributionCenterSubmit } from 'domains/Catalog/Products/tracking';
import useLocationListToast from 'domains/Shipping/Locations/hooks/useLocationListToast';
import useLocations from 'domains/Shipping/Locations/hooks/useLocations';
import DeleteLocationAlert from './components/DeleteLocationAlert';
import DeleteLocationModal from './components/DeleteLocationModal/DeleteLocationModal';
import DeleteLocationStepsModal, {
  DeleteLocationParams,
} from './components/DeleteLocationStepsModal/DeleteLocationStepsModal';
import SetLocationMainModal from './components/SetLocationMainModal';
import useDeleteLocationJob from '../../useDeleteLocationJob';
import LocationsListDesktop from '../LocationsListDesktop';
import LocationsListMobile from '../LocationsListMobile';
import LocationsSortableList from '../LocationSortableList';

interface LocationsListProps {
  isLoading: boolean;
  isSorting: boolean;
  handleIsSorting: () => void;
}

// eslint-disable-next-line max-statements
function LocationsList({
  isLoading,
  isSorting,
  handleIsSorting,
}: Readonly<LocationsListProps>): JSX.Element {
  const {
    locations,
    updateLocationsOrdering,
    updateMainLocation,
    deleteLocation,
    deleteLocationByJob,
  } = useLocations();

  const { onLoading, onSuccess, onError } = useLocationListToast();
  const { removeLocationFromFilters } = useInventoryAppliedFilters();
  const [locationsList, setLocationsList] = useState<LocationResponseDto[]>([]);
  const [isOpenSetMainModal, openSetMainModal, closeSetMainModal] = useModal();
  const [isOpenDeleteModal, openDeleteModal, closeDeleteModal] = useModal();
  const [selectedLocation, setSelectedLocation] = useState<string>();
  const [hasTransferStock] = useHasTags([CATALOG_STOCK_TRANSFER]);
  const { setJob, removeJob } = useDeleteLocationJob();

  const [handleUpdateLocationsOrdering, isSavingOrdering] = useAsyncFunc(
    async () => {
      const newLocationsOrdering = locationsList.map(
        ({ id, priority }) =>
          ({
            locationId: id,
            priority: priority,
          } as LocationPriorityRequestDto),
      );
      await updateLocationsOrdering(newLocationsOrdering);
    },
    () => {
      handleIsSorting();
      onSuccess('sort');
    },
    () => onError('sort'),
  );

  const [handleSaveMainLocation, isSavingMainLocation] = useAsyncFunc(
    async () => {
      if (selectedLocation) await updateMainLocation(selectedLocation);
    },
    () => {
      closeSetMainModal();
      onSuccess('setAsMain');
    },
    () => onError('setAsMain'),
  );

  const handleDeleteLocationSteps = (params: DeleteLocationParams) => {
    closeDeleteModal();
    const { process, locationId } = params;
    switch (process) {
      case 'sync':
        handleDeleteLocation(locationId);
        trackingShippingDeleteDistributionCenterSubmit({
          locationId,
          job: false,
        });
        break;
      case 'job': {
        handleDeleteLocationJob(params);
        trackingShippingDeleteDistributionCenterSubmit({
          ...params,
          job: true,
        });
        break;
      }
    }
  };

  const [handleDeleteLocationJob, isDeletingLocationJob] = useAsyncFunc<
    DeleteLocationParams,
    Promise<void>
  >(
    async (params) => {
      if (!params) return;
      const { operationType, locationId, locationDestinationId } = params;
      const { token } = await deleteLocationByJob({
        locationId,
        operationType,
        locationDestinationId,
      });
      setJob(token, {
        operationType: operationType.toLowerCase(),
        locationId,
        sourceName: locations.find((location) => location.id === locationId)
          ?.name,
        destinationName: locations.find(
          (location) => location.id === locationDestinationId,
        )?.name,
      });
    },
    () => onSuccess(),
    () => onError('delete'),
  );

  const [handleDeleteLocation, isDeletingLocation] = useAsyncFunc(
    async () => {
      if (selectedLocation) await deleteLocation(selectedLocation);
    },
    () => {
      onSuccess('delete');
      removeLocationFromFilters();
    },
    () => onError('delete'),
  );

  const handleOnChange = (newOrder) => {
    const newLocationsList = newOrder.map((location, index) => ({
      ...location,
      priority: index,
      main: index === 0,
    }));
    setLocationsList(newLocationsList);
  };

  const handleOpenSetMainModal = (locationId) => {
    setSelectedLocation(locationId);
    openSetMainModal();
  };

  const handleOpenDeleteModal = (locationId) => {
    removeJob();
    setSelectedLocation(locationId);
    openDeleteModal();
  };

  const handleCloseSorting = () => {
    handleIsSorting();
    setLocationsList([]);
  };

  const getDeleteLocationModal = (locationId: string) => {
    if (hasTransferStock) {
      return (
        <DeleteLocationStepsModal
          onClose={closeDeleteModal}
          locationsList={locationsList}
          locationId={locationId}
          onConfirm={handleDeleteLocationSteps}
        />
      );
    }
    return (
      <DeleteLocationModal
        locationId={locationId}
        isLoadingLocations={isLoading}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteLocation}
      />
    );
  };

  useEffect(() => {
    setLocationsList(locations);
  }, [locations]);

  useEffect(() => {
    if (isSavingMainLocation) onLoading('setAsMain');
    if (isSavingOrdering) onLoading('sort');
    if (isDeletingLocation) onLoading('delete');
    if (isDeletingLocationJob) onLoading('delete');
  }, [
    isDeletingLocation,
    isDeletingLocationJob,
    isSavingMainLocation,
    isSavingOrdering,
    onLoading,
  ]);

  return (
    <>
      <DeleteLocationAlert />
      {((locations.length > 0 && !isLoading) || isLoading) && !isSorting && (
        <Responsive
          mobile={
            <LocationsListMobile
              openSetMainModal={handleOpenSetMainModal}
              openDeleteModal={handleOpenDeleteModal}
            />
          }
          desktop={
            <LocationsListDesktop
              openSetMainModal={handleOpenSetMainModal}
              openDeleteModal={handleOpenDeleteModal}
            />
          }
        />
      )}

      {locationsList.length > 0 && isSorting && (
        <>
          <LocationsSortableList
            locationsList={locationsList}
            handleOnChange={handleOnChange}
          />
          <CancelAndConfirmButtons
            isConfirmDisabled={locationsList === locations}
            onCancel={handleCloseSorting}
            onConfirm={handleUpdateLocationsOrdering}
          />
        </>
      )}
      {!!selectedLocation && isOpenSetMainModal && (
        <SetLocationMainModal
          locationId={selectedLocation}
          isLoading={isLoading}
          onClose={closeSetMainModal}
          onConfirm={handleSaveMainLocation}
        />
      )}
      {!!selectedLocation &&
        isOpenDeleteModal &&
        getDeleteLocationModal(selectedLocation)}
    </>
  );
}

export default LocationsList;
